import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CalendarIcon, 
  VideoCameraIcon, 
  CheckCircleIcon, 
  ClockIcon, 
  BuildingOfficeIcon,
  XMarkIcon,
  EnvelopeIcon,
  UserIcon,
  MapPinIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

interface MeetingRequestFormProps {
  onClose?: () => void;
}

const MeetingRequestForm: React.FC<MeetingRequestFormProps> = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    preferredDate: '',
    preferredTime: '',
    meetingType: 'virtual' as 'virtual' | 'presencial',
    message: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestId, setRequestId] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar errores al escribir
    if (error) setError(null);
  };

  const handleMeetingTypeChange = (type: 'virtual' | 'presencial') => {
    setFormData(prev => ({ ...prev, meetingType: type }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('El nombre es requerido');
      return false;
    }
    
    if (!formData.email.trim()) {
      setError('El email es requerido');
      return false;
    }
    
    if (!formData.email.includes('@')) {
      setError('Ingresa un email válido');
      return false;
    }
    
    return true;
  };

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  
  if (!validateForm()) {
    return;
  }
  
  setIsSubmitting(true);
  setError(null);
  
  try {
    console.log('🚀 Enviando solicitud...');
    
    // ✅ QUITAR EL TIMEOUT o aumentarlo a 30 segundos
 const response = await fetch('/api/send-meeting-request', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
      // ❌ NO usar signal: controller.signal (eliminar esto)
    });

    console.log('📡 Status recibido:', response.status);
    
    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ Respuesta exitosa:', data);

    if (!data.success) {
      throw new Error(data.message || 'Error en la respuesta del servidor');
    }

    // ✅ ESTAS SON LAS LÍNEAS CRÍTICAS
    setRequestId(data.data?.requestId || null);
    setIsSubmitted(true);
    
    console.log('🎉 Estado actualizado - isSubmitted debería ser true');

    // Reiniciar formulario después de 3 segundos
    setTimeout(() => {
      setFormData({
        name: '',
        email: '',
        company: '',
        preferredDate: '',
        preferredTime: '',
        meetingType: 'virtual',
        message: ''
      });
    }, 3000);

  } catch (err) {
    console.error('❌ Error:', err);
    setError(err instanceof Error ? err.message : 'Error al enviar la solicitud');
  } finally {
    setIsSubmitting(false);
  }
};

  // Opciones de horarios disponibles
  const availableTimes = [
    '09:00', '10:00', '11:00', 
    '14:00', '15:00', '16:00', '17:00'
  ];

  // Fecha mínima (mañana)
  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  if (isSubmitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="p-8 text-center"
      >
        <div className="w-20 h-20 bg-gradient-to-r from-green-500 via-teal-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg">
          <CheckCircleIcon className="h-10 w-10 text-white" />
        </div>
        
        <h3 className="text-2xl font-bold text-gray-900 mb-4">¡Solicitud Enviada!</h3>
        <p className="text-gray-600 mb-6">
          Hemos recibido tu solicitud de reunión. Te contactaremos en las próximas 24 horas hábiles.
        </p>
        
        {requestId && (
          <div className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium">
            <InformationCircleIcon className="h-4 w-4" />
            ID: {requestId}
          </div>
        )}
        
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 border border-gray-200 mb-8">
          <h4 className="font-bold text-gray-900 mb-3">Próximos pasos:</h4>
          <ul className="text-sm text-gray-600 space-y-2 text-left">
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span>Recibirás un email de confirmación en <strong>{formData.email}</strong></span>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
              <span>Coordinaremos detalles de la reunión</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
              <span>
                {formData.meetingType === 'virtual' 
                  ? 'Te enviaremos el link de acceso' 
                  : 'Te enviaremos la dirección en Córdoba'
                }
              </span>
            </li>
          </ul>
        </div>
        
        <button
          type="button"
          onClick={onClose}
          className="px-6 py-3 bg-gradient-to-r from-blue-600 to-green-500 text-white font-medium rounded-lg hover:from-green-500 hover:to-blue-600 transition-all duration-300"
        >
          Cerrar
        </button>
      </motion.div>
    );
  }

  return (
    <div className="p-6 sm:p-8 max-h-[90vh] overflow-y-auto">
      {/* Encabezado */}
      <div className="mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
              Solicitar Reunión
            </h3>
            <p className="text-gray-600">
              Completa el formulario para agendar una reunión con Algorithmia
            </p>
          </div>
          
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>
        
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
      </div>
      
      {/* Formulario */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Información básica */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Nombre */}
          <div className="space-y-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Nombre *
            </label>
            <div className="relative">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 placeholder-gray-400"
                placeholder="Tu nombre completo"
              />
              <UserIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>
          
          {/* Email */}
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email *
            </label>
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 placeholder-gray-400"
                placeholder="ejemplo@empresa.com"
              />
              <EnvelopeIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
        
        {/* Empresa */}
        <div className="space-y-2">
          <label htmlFor="company" className="block text-sm font-medium text-gray-700">
            Empresa (opcional)
          </label>
          <div className="relative">
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 placeholder-gray-400"
              placeholder="Nombre de tu empresa"
            />
            <BuildingOfficeIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
        </div>
        
        {/* Fecha y hora */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          {/* Fecha */}
          <div className="space-y-2">
            <label htmlFor="preferredDate" className="block text-sm font-medium text-gray-700">
              Fecha preferida (opcional)
            </label>
            <div className="relative">
              <input
                type="date"
                id="preferredDate"
                name="preferredDate"
                value={formData.preferredDate}
                onChange={handleChange}
                min={getMinDate()}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-gray-700"
              />
              <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>
          
          {/* Hora */}
          <div className="space-y-2">
            <label htmlFor="preferredTime" className="block text-sm font-medium text-gray-700">
              Hora preferida (opcional)
            </label>
            <div className="relative">
              <select
                id="preferredTime"
                name="preferredTime"
                value={formData.preferredTime}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 text-gray-700 appearance-none bg-white"
              >
                <option value="">Seleccionar hora</option>
                {availableTimes.map(time => (
                  <option key={time} value={time}>
                    {time} hs
                  </option>
                ))}
              </select>
              <ClockIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>
          </div>
        </div>
        
        {/* Tipo de reunión */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Tipo de reunión
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <motion.button
              type="button"
              onClick={() => handleMeetingTypeChange('virtual')}
              className={`p-4 border-2 rounded-xl flex flex-col items-center justify-center gap-3 transition-all duration-300 ${
                formData.meetingType === 'virtual'
                  ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-lg'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={`p-3 rounded-full ${
                formData.meetingType === 'virtual'
                  ? 'bg-blue-100 text-blue-600'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                <VideoCameraIcon className="h-6 w-6" />
              </div>
              <span className="font-medium">Virtual</span>
              <span className="text-xs text-gray-500">Google Meet / Zoom</span>
            </motion.button>
            
            <motion.button
              type="button"
              onClick={() => handleMeetingTypeChange('presencial')}
              className={`p-4 border-2 rounded-xl flex flex-col items-center justify-center gap-3 transition-all duration-300 ${
                formData.meetingType === 'presencial'
                  ? 'border-green-500 bg-green-50 text-green-700 shadow-lg'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className={`p-3 rounded-full ${
                formData.meetingType === 'presencial'
                  ? 'bg-green-100 text-green-600'
                  : 'bg-gray-100 text-gray-500'
              }`}>
                <MapPinIcon className="h-6 w-6" />
              </div>
              <span className="font-medium">Presencial</span>
              <span className="text-xs text-gray-500">Córdoba, Argentina</span>
            </motion.button>
          </div>
        </div>
        
        {/* Comentarios */}
        <div className="space-y-2">
          <label htmlFor="message" className="block text-sm font-medium text-gray-700">
            Comentarios adicionales (opcional)
          </label>
          <textarea
            id="message"
            name="message"
            value={formData.message}
            onChange={handleChange}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-300 placeholder-gray-400 resize-none min-h-[100px]"
            placeholder="Objetivos, temas a tratar, información adicional..."
            maxLength={1000}
          />
          <div className="text-xs text-gray-500 text-right">
            {formData.message.length}/1000 caracteres
          </div>
        </div>
        
        {/* Información */}
        <div className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 border border-gray-200">
          <h4 className="font-bold text-gray-900 mb-2 text-sm">¿Qué incluye la reunión?</h4>
          <ul className="text-sm text-gray-600 space-y-1">
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
              <span>Análisis de necesidades y objetivos</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
              <span>Evaluación de posibles soluciones</span>
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 bg-teal-500 rounded-full"></div>
              <span>Estimación preliminar de tiempo y costos</span>
            </li>
          </ul>
        </div>
        
        {/* Botones */}
        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <XMarkIcon className="h-5 w-5" />
              Cancelar
            </button>
          )}
          
          <motion.button
            type="submit"
            disabled={isSubmitting}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`px-6 py-3 bg-gradient-to-r from-blue-600 to-green-500 text-white font-medium rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 ${
              isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:from-green-500 hover:to-blue-600'
            } ${onClose ? 'flex-1' : 'w-full'}`}
          >
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Enviando...
              </>
            ) : (
              <>
                <CalendarIcon className="h-5 w-5" />
                Solicitar Reunión
              </>
            )}
          </motion.button>
        </div>
        
        {/* Contacto */}
        <div className="text-center pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500">
            ¿Necesitas ayuda?{' '}
            <a 
              href="mailto:contacto@algorithmia.com.ar" 
              className="text-blue-600 hover:text-blue-700 font-medium hover:underline"
            >
              contacto@algorithmia.com.ar
            </a>
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Respondemos en menos de 24 horas hábiles
          </p>
        </div>
      </form>
    </div>
  );
};

export default MeetingRequestForm;